import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';

import {
  fetchDailyDealsAction,
  fetchDailyDealsAssetsAction,
  refreshDailyDealsUpdateTokenAction,
  resetDailyDealsDataAction,
} from 'actions/daily-deals.actions';

import SpecialOffersLayout from 'components/specialOffersLayout/SpecialOffersLayout';

import { useDailyDealsEvents } from 'hooks/events/useDailyDealsEvents';
import { useLoadingTime } from 'hooks/useLoadingTime';

import DailyDealsSection from './dailyDealsSection/DailyDealsSection';

const DailyDeals = () => {
  const dispatch = useDispatch();
  const { deals, dealsLoading, dealsValidUntil, dealsAssets, updateToken } = useSelector(
    (state: IState) => state.dailyDeals
  );
  const dataAvailable = !!deals?.length && !!dealsAssets.length;
  const dataNotAvailable = !deals?.length && !dealsAssets.length;

  const { loadingTime, handleStartLoadingTime, handleStopLoadingTime } = useLoadingTime();

  const { sendDailyDealsGetEvent } = useDailyDealsEvents();

  useEffect(() => {
    dealsLoading ? handleStartLoadingTime() : handleStopLoadingTime();
  }, [dealsLoading]);

  useEffect(() => {
    dataAvailable && loadingTime && loadingTime > 0 && sendDailyDealsGetEvent({ time: loadingTime });
  }, [dataAvailable, loadingTime]);

  useEffect(() => {
    dispatch(fetchDailyDealsAction(new AbortController().signal));
  }, [dispatch, updateToken]);

  useEffect(() => {
    if (!dealsValidUntil) {
      return;
    }

    const expirationTime = DateTime.fromMillis(dealsValidUntil).diffNow().milliseconds;
    const timer = setTimeout(() => {
      dispatch(refreshDailyDealsUpdateTokenAction());
    }, expirationTime);

    return () => {
      clearTimeout(timer);
    };
  }, [dispatch, dealsValidUntil]);

  useEffect(() => {
    if (deals.length) {
      dispatch(fetchDailyDealsAssetsAction(deals.map(({ asset }) => asset.id)));
    }
  }, [dispatch, deals]);

  useEffect(() => {
    return () => {
      dispatch(resetDailyDealsDataAction());
    };
  }, [dispatch]);

  return (
    <SpecialOffersLayout
      title="Daily package deal"
      subtitle="Get them before they are gone! Daily Offer rotates landmarks, cities, and countries daily, and it is the main way to acquire additional shares from the primary market at the lowest price. Once sold, they are gone."
      text="Discount to the standard price"
      dataLoading={dealsLoading}
      dataAvailable={dataAvailable}
      dataNotAvailable={dataNotAvailable}
      endTimestamp={dealsValidUntil ?? 0}
      className="lg:tw-static"
    >
      <DailyDealsSection />
    </SpecialOffersLayout>
  );
};

export default DailyDeals;
